import { uniq } from "lodash";
import { CommunicationChannelType, Guest, Message, MessageFormatType, MessageType, Seller, User } from "types";
import { getGuestPhone } from "./Guest";

/** Shortest local number a guest can type without a country code, so a short fragment never suffix-matches. */
const MIN_LOCAL_PHONE_DIGITS = 10;

interface isSentProps {
    message: Message;
    seller: Seller;
    user: User;
}

export const isSent = ({ message, seller, user }: isSentProps) => {
    const isReceiver = message.to?.find((participant) => participant.id === user.id || participant.id === seller.id);
    return message.from === user.id || !isReceiver;
};

/** Legacy messages carry no formats, so only their `type` says whether they went out as a text. */
export const isSmsMessage = ({ formats, channels, type }: Pick<Message, "formats" | "channels" | "type">) => {
    const messageFormats = formats ?? [];
    const isLegacy = messageFormats.length === 0;
    const hasSmsChannel = !!channels?.includes(CommunicationChannelType.SMS);
    const hasTextFormat = messageFormats.some((format) => format.type === MessageFormatType.TEXT);

    return hasSmsChannel && (hasTextFormat || (isLegacy && type === MessageType.SMS));
};

/** Only outbound SMS gets delivery reports; inbound texts and other channels never do. */
export const isOutboundSms = ({ message, seller, user }: isSentProps) => {
    return isSmsMessage(message) && isSent({ message, seller, user });
};

/** "00" is the international dialling prefix, a single leading "0" a national trunk prefix (UK "07700 900123"). */
const stripDialPrefix = (digits: string) => {
    if (digits.startsWith("00")) {
        return digits.slice(2);
    }

    return digits.startsWith("0") ? digits.slice(1) : digits;
};

const isDigitsMatch = (digits: string, sentFromNormalized: string) => {
    const isLocalMatch = digits.length >= MIN_LOCAL_PHONE_DIGITS && sentFromNormalized.endsWith(digits);
    return digits === sentFromNormalized || isLocalMatch;
};

const isSamePhone = (guestPhone: string, sentFromNormalized: string) => {
    const digits = guestPhone.replace(/\D/g, "");
    if (!digits) {
        return false;
    }

    // Raw digits too: some countries keep the leading 0 in E.164 (Italian landline "+39 06...").
    return isDigitsMatch(digits, sentFromNormalized) || isDigitsMatch(stripDialPrefix(digits), sentFromNormalized);
};

/**
 * The name of the one guest whose questionnaire phone sent this reply. The sender's customer record can carry another
 * name when the guest's phone matched an existing customer, so the guest name is what the seller recognises.
 */
export const getInboundSenderName = (message: Message, guests: Guest[] = []) => {
    const sentFromNormalized = message.inbound?.sentFromNormalized;
    if (!sentFromNormalized) {
        return undefined;
    }

    const names = guests
        .filter((guest) => guest.name && isSamePhone(getGuestPhone(guest), sentFromNormalized))
        .map((guest) => guest.name);
    const uniqueNames = uniq(names);

    return uniqueNames.length === 1 ? uniqueNames[0] : undefined;
};
